import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { IMAGES, SITE } from "../config/site";

const TEAM = [
  {
    name: "Ronald K. Nolen, Esq.",
    title: "Founder & Managing Attorney",
    image: IMAGES.ronald,
    objectPos: "object-[center_top]",
    desc:
      "Chicago-born attorney, educator, negotiator, and advocate with broad experience across litigation, real estate, contracts, business matters, and sports and entertainment law.",
    to: "/team/ronald-k-nolen",
    testid: "team-ronald",
  },
  {
    name: "Valencia King, Esq.",
    title: SITE.valenciaTitle,
    image: IMAGES.valencia,
    // Valencia-specific focal point: shift crop upward so her full face
    // (forehead, eyes, nose, mouth, chin) stays visible above the content
    // section on desktop/laptop. Do NOT change Ronald's card.
    objectPos: "object-[center_25%]",
    desc:
      "A legal professional focused on helping creatives and entrepreneurs navigate trademark searches, applications, and responses before the United States Patent and Trademark Office.",
    to: "/team/valencia-king",
    testid: "team-valencia",
  },
];

export default function Team() {
  useSeo({
    title: "Meet Our Team | The Law Office of Ronald K. Nolen, LLC",
    description:
      "Meet the experienced legal team at The Law Office of Ronald K. Nolen, LLC — strategic Chicago counsel committed to protecting clients' interests.",
    path: "/team",
  });

  return (
    <>
      <section className="relative overflow-hidden bg-warmwhite pt-36 pb-16 md:pt-44 md:pb-20" data-testid="team-hero">
        <div className="arch-grid pointer-events-none absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="text-xs font-600 uppercase tracking-label text-gold">Our People</p>
            <h1 className="mt-4 max-w-4xl font-serif text-5xl font-500 leading-[1.02] text-navy sm:text-6xl">
              Meet Our Legal Team
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-bluegray-text">
              Experienced, strategic, and committed to helping clients protect their interests and
              pursue meaningful opportunities.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-warmwhite pb-24" data-testid="team-cards">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid gap-10 md:grid-cols-2 md:gap-8">
            {TEAM.map((m, i) => (
              <Reveal key={m.testid} delay={i * 0.12}>
                <article className="group flex h-full flex-col overflow-hidden rounded-sm border border-navy/10 bg-white" data-testid={m.testid}>
                  <div className="relative overflow-hidden bg-cream">
                    <img
                      src={m.image}
                      alt={`${m.name}, ${m.title}`}
                      className={`h-[26rem] w-full object-cover ${m.objectPos} transition-transform duration-700 group-hover:scale-[1.03]`}
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-8">
                    <h2 className="font-serif text-3xl font-500 text-navy">{m.name}</h2>
                    <p className="mt-1 text-sm font-600 uppercase tracking-wide text-gold">{m.title}</p>
                    <p className="mt-4 flex-1 text-base leading-relaxed text-bluegray-text">{m.desc}</p>
                    <Link to={m.to} data-testid={`${m.testid}-bio-link`}
                      className="group/link mt-7 inline-flex items-center gap-2 rounded-sm border border-navy/20 px-6 py-3 text-sm font-600 text-navy transition-colors hover:border-gold hover:bg-navy hover:text-white">
                      View Biography
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" strokeWidth={1.8} />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Get Started"
        headline="Need help with a legal matter?"
        copy="Share a brief overview of your matter and the firm will review your inquiry."
        showCall={false}
        testid="team-cta"
      />
    </>
  );
}
